// /organizations — the access-policy manager UI.
//
// Left column: organizations (create — platform_admin only).
// Right column: selected org's members (add/re-role/remove — org_admin of the
// org or platform_admin) and the org's projects (move across orgs —
// platform_admin, or org_admin of both orgs) and duplicate (a full copy — same rule).
//
// All authorization is enforced server-side; this page only *hides* controls
// the current user's role can't use. Roles come from /api/auth/me (user.orgs).
import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { listOrgs, createOrg, listOrgMembers, addOrgMember, orgAccountExists, removeOrgMember, moveProjectToOrg, duplicateProject, getProjectJob, updateOrg, deleteOrg, updateOrgMember, setAttributeProvider, deleteUserAccount, getEntitlements, setEntitlements } from "../services/orgs";
import { listProjects } from "../services/projects";
import { Building2, Plus, X, Loader2, Trash2, UserPlus, FolderOpen, ShieldCheck, Pencil, Plug, UserX, Copy, ArrowRight, CheckCircle2, AlertTriangle, Circle } from "lucide-react";
import { useTutorialDemo } from "../components/tutorial/useTutorialDemo";
import PermissionsMatrix from "../components/PermissionsMatrix";

const ROLE_OPTIONS = ["org_admin", "member", "app_user"];
const ROLE_BADGE = {
  platform_admin: "bg-purple-100 text-purple-800",
  org_admin: "bg-blue-100 text-blue-800",
  member: "bg-slate-100 text-slate-700",
  app_user: "bg-amber-100 text-amber-800",
};

export default function Organizations() {
  const { user } = useAuth();
  const isPlatformAdmin = !!user?.orgs?.some((o) => o.role === "platform_admin");
  const adminOf = (slug) => isPlatformAdmin || !!user?.orgs?.some((o) => o.org === slug && o.role === "org_admin");

  const [orgs, setOrgs] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreate, setShowCreate] = useState(false);

  // Tutorial demo instructions: the tour opens these modals for DISPLAY
  // (prefilled with fictional sample values, submit disabled) so it can
  // spotlight the actual fields. Deliberately NOT tied to any real org —
  // the sample org is a placeholder and nothing is submitted in demo mode.
  const demo = useTutorialDemo(["new-org", "add-member", "policy-integration"]);
  const demoOrg = { slug: "example_corp", name: "Example Corp", attribute_provider: null };

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const [o, p] = await Promise.all([listOrgs(), listProjects()]);
      setOrgs(o);
      setProjects(p);
      setSelected((cur) => cur && o.some((x) => x.slug === cur) ? cur : (o[0]?.slug || null));
    } catch (e) {
      setError(e?.message || "Failed to load organizations");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const selectedOrg = orgs.find((o) => o.slug === selected) || null;

  return (
    <div className="pt-[30px] pb-24 md:pb-[30px] px-4 md:px-[60px] bg-page-bg min-h-full flex flex-col gap-5" data-tour="page-organizations">
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-3">
        <div className="flex flex-col gap-1.5">
          <h1 className="text-[32px] font-medium leading-tight text-page-title">Organizations</h1>
          <p className="text-sm font-normal text-page-muted">
            Access policies live here: each organization owns its projects, and its members only see what the organization owns. Roles: <em>org_admin</em> manages the org and its projects, <em>member</em> works inside them, <em>app_user</em> only opens published apps.
          </p>
        </div>
        {isPlatformAdmin && (
          <button
            onClick={() => setShowCreate(true)}
            data-testid="create-org"
            className="flex items-center gap-2 h-9 px-4 rounded-[5px] bg-nav-active text-white text-sm font-medium cursor-pointer hover:bg-nav-active-dark transition-colors mt-1"
          >
            <Plus size={20} /> New Organization
          </button>
        )}
      </div>

      {error && <div className="bg-red-50 text-red-800 px-4 py-2 rounded-[5px] text-sm">{error}</div>}
      {loading && <div className="text-page-muted text-sm"><Loader2 className="inline animate-spin mr-2" size={16}/>Loading…</div>}

      {!loading && (
        <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-5 items-start">
          {/* Org list */}
          <div className="bg-card-bg rounded-[8px] overflow-hidden">
            {orgs.length === 0 && <div className="px-4 py-6 text-sm text-page-muted">No organizations visible.</div>}
            {orgs.map((o) => (
              <button
                key={o.slug}
                onClick={() => setSelected(o.slug)}
                data-testid={`org-${o.slug}`}
                className={`w-full flex items-center gap-3 px-4 py-3 text-left border-b border-page-divider last:border-b-0 transition-colors ${selected === o.slug ? "bg-page-bg" : "hover:bg-page-bg/50"}`}
              >
                <Building2 size={18} className="shrink-0 text-page-muted" />
                <span className="flex-1 min-w-0">
                  <span className="block text-sm font-medium truncate">{o.name}</span>
                  <span className="block text-xs text-page-muted font-mono">{o.slug}</span>
                </span>
                <span className="text-xs text-page-muted">{o.member_count} member{o.member_count === 1 ? "" : "s"}</span>
              </button>
            ))}
          </div>

          {/* Selected org detail */}
          {selectedOrg ? (
            <OrgDetail
              key={selectedOrg.slug}
              org={selectedOrg}
              canAdmin={adminOf(selectedOrg.slug)}
              isPlatformAdmin={isPlatformAdmin}
              allOrgs={orgs}
              adminOf={adminOf}
              projects={projects.filter((p) => (p.org_slug || "mindshine") === selectedOrg.slug)}
              onChanged={load}
            />
          ) : (
            <div className="bg-card-bg rounded-[8px] p-8 text-sm text-page-muted">Select an organization.</div>
          )}
        </div>
      )}

      {showCreate && (
        <CreateOrgModal
          onClose={() => setShowCreate(false)}
          onSuccess={(slug) => { setShowCreate(false); setSelected(slug); load(); }}
        />
      )}

      {/* Tutorial demo modals — display-only, fictional sample data, no submit. */}
      {demo === "new-org" && (
        <CreateOrgModal tutorialDemo onClose={() => {}} onSuccess={() => {}} />
      )}
      {demo === "add-member" && (
        <AddMemberModal tutorialDemo orgSlug={demoOrg.slug} isPlatformAdmin={isPlatformAdmin} onClose={() => {}} onSuccess={() => {}} />
      )}
      {demo === "policy-integration" && (
        <ProviderModal tutorialDemo org={demoOrg} onClose={() => {}} onSuccess={() => {}} />
      )}
    </div>
  );
}

function OrgDetail({ org, canAdmin, isPlatformAdmin, allOrgs, adminOf, projects, onChanged }) {
  const [members, setMembers] = useState(null);
  const [err, setErr] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [busyEmail, setBusyEmail] = useState(null);
  const [showRename, setShowRename] = useState(false);
  const [showProvider, setShowProvider] = useState(false);
  const [showDeleteOrg, setShowDeleteOrg] = useState(false);
  const [editingMember, setEditingMember] = useState(null);

  const loadMembers = async () => {
    setErr(null);
    try { setMembers(await listOrgMembers(org.slug)); }
    catch (e) {
      // Non-admin members can't list members — show the projects only.
      setMembers([]);
      if (canAdmin) setErr(e?.message || "Failed to load members");
    }
  };
  useEffect(() => { loadMembers(); }, [org.slug]); // eslint-disable-line

  const changeRole = async (email, role) => {
    setBusyEmail(email); setErr(null);
    try { await addOrgMember(org.slug, { email, role }); await loadMembers(); }
    catch (e) { setErr(e?.message || "Role change failed"); }
    finally { setBusyEmail(null); }
  };
  const remove = async (email) => {
    setBusyEmail(email); setErr(null);
    try { await removeOrgMember(org.slug, email); await loadMembers(); onChanged(); }
    catch (e) { setErr(e?.message || "Remove failed"); }
    finally { setBusyEmail(null); }
  };
  // Move / Duplicate: explicit buttons and dialogs (the old silent "Owner" dropdown moved a
  // project on change, with no confirmation — easy to miss and easy to trigger by accident)
  const [moving, setMoving] = useState(null);
  const [duplicating, setDuplicating] = useState(null);
  const moveTargets = (allOrgs || []).filter((o) => o.slug !== org.slug && (adminOf ? adminOf(o.slug) : isPlatformAdmin));
  const copyTargets = (allOrgs || []).filter((o) => adminOf ? adminOf(o.slug) : isPlatformAdmin);

  return (
    <div className="flex flex-col gap-5 min-w-0">
      {err && <div className="bg-red-50 text-red-800 px-4 py-2 rounded-[5px] text-sm">{err}</div>}

      {/* Members */}
      <div className="bg-card-bg rounded-[8px] overflow-hidden" data-tour="org-members">
        <div className="flex items-center justify-between px-5 py-3 border-b border-page-divider">
          <h2 className="text-base font-semibold flex items-center gap-2">
            <ShieldCheck size={16}/> Members — {org.name}
            {org.status !== "active" && <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-800">{org.status}</span>}
          </h2>
          {canAdmin && (
            <div className="flex items-center gap-2">
              <button onClick={() => setShowRename(true)} data-testid="rename-org" title="Rename organization"
                className="flex items-center gap-1.5 h-8 px-3 rounded-[5px] border border-page-divider text-xs hover:bg-page-bg">
                <Pencil size={13}/> Rename
              </button>
              <button onClick={() => setShowProvider(true)} data-testid="policy-integration" data-tour="org-policy-integration" title="Connect the customer's existing policies/entitlement API"
                className="flex items-center gap-1.5 h-8 px-3 rounded-[5px] border border-page-divider text-xs hover:bg-page-bg">
                <Plug size={13}/> Policy integration{org.attribute_provider ? " ✓" : ""}
              </button>
              {isPlatformAdmin && org.slug !== "mindshine" && (
                <button onClick={() => setShowDeleteOrg(true)} data-testid="delete-org" title="Delete organization"
                  className="flex items-center gap-1.5 h-8 px-3 rounded-[5px] border border-red-200 text-red-600 text-xs hover:bg-red-50">
                  <Trash2 size={13}/> Delete
                </button>
              )}
              <button
                onClick={() => setShowAdd(true)}
                data-testid="add-member"
                className="flex items-center gap-1.5 h-8 px-3 rounded-[5px] bg-nav-active text-white text-xs font-medium hover:bg-nav-active-dark transition-colors"
              >
                <UserPlus size={14}/> Add member
              </button>
            </div>
          )}
        </div>
        <table className="w-full text-sm">
          <thead className="text-left text-page-muted">
            <tr className="border-b border-page-divider">
              <th className="px-5 py-2.5 font-medium">Email</th>
              <th className="px-5 py-2.5 font-medium">Name</th>
              <th className="px-5 py-2.5 font-medium">Role</th>
              <th className="px-5 py-2.5 font-medium w-[90px]"></th>
            </tr>
          </thead>
          <tbody>
            {members === null ? (
              <tr><td colSpan={4} className="px-5 py-5 text-center text-page-muted"><Loader2 className="inline animate-spin mr-2" size={14}/>Loading…</td></tr>
            ) : members.length === 0 ? (
              <tr><td colSpan={4} className="px-5 py-5 text-center text-page-muted">{canAdmin ? "No members." : "Member list is visible to org admins."}</td></tr>
            ) : members.map((m) => (
              <tr key={m.email} className="border-b border-page-divider last:border-b-0">
                <td className="px-5 py-2.5 font-mono text-xs">{m.email}</td>
                <td className="px-5 py-2.5 text-page-muted">{[m.first_name, m.last_name].filter(Boolean).join(" ") || "—"}</td>
                <td className="px-5 py-2.5">
                  {canAdmin && m.role !== "platform_admin" ? (
                    <select
                      value={m.role}
                      disabled={busyEmail === m.email}
                      onChange={(e) => changeRole(m.email, e.target.value)}
                      className="h-7 px-2 rounded border border-page-divider bg-page-bg text-xs"
                    >
                      {ROLE_OPTIONS.map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>
                  ) : (
                    <span className={`text-xs px-2 py-0.5 rounded-full ${ROLE_BADGE[m.role] || ROLE_BADGE.member}`}>{m.role}</span>
                  )}
                </td>
                <td className="px-5 py-2.5">
                  <div className="flex items-center gap-1">
                    {canAdmin && (m.role !== "platform_admin" || isPlatformAdmin) && (
                      <button
                        onClick={() => setEditingMember(m)}
                        className="p-1.5 rounded hover:bg-page-bg text-page-muted"
                        title="Edit account, password, and subject attributes"
                        data-testid={`edit-member-${m.email}`}
                      >
                        <Pencil size={15}/>
                      </button>
                    )}
                    {canAdmin && m.role !== "platform_admin" && (
                      <button
                        onClick={() => remove(m.email)}
                        disabled={busyEmail === m.email}
                        className="p-1.5 rounded hover:bg-red-50 text-red-600 disabled:opacity-50"
                        title="Remove from organization"
                      >
                        <Trash2 size={15}/>
                      </button>
                    )}
                    {isPlatformAdmin && m.role !== "platform_admin" && (
                      <button
                        onClick={() => deleteUserAccount(m.email).then(() => { loadMembers(); onChanged(); }).catch((e) => setErr(e?.message || "Account delete failed"))}
                        className="p-1.5 rounded hover:bg-red-50 text-red-700"
                        title="Delete user ACCOUNT globally (all org memberships)"
                      >
                        <UserX size={15}/>
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* What each role may do — the read-only authorization reference, parked
          directly under the roles it explains. Collapsed by default: reference
          material, not something the page should lead with. */}
      <PermissionsMatrix />

      {/* Plan & add-ons (unified auth, step 3): which add-ons the PLATFORM has
          granted this organization. Nothing configured = everything on. Only a
          platform admin toggles; the org's own admins and members see the state. */}
      <PlanAddons org={org} canAdmin={isPlatformAdmin} />

      {/* Projects owned by this org */}
      <div className="bg-card-bg rounded-[8px] overflow-hidden" data-tour="org-projects">
        <div className="px-5 py-3 border-b border-page-divider">
          <h2 className="text-base font-semibold flex items-center gap-2"><FolderOpen size={16}/> Projects</h2>
        </div>
        {projects.length === 0 ? (
          <div className="px-5 py-5 text-sm text-page-muted">No projects owned by this organization.</div>
        ) : (
          <table className="w-full text-sm">
            <tbody>
              {projects.map((p) => (
                <tr key={p.slug} className="border-b border-page-divider last:border-b-0">
                  <td className="px-5 py-2.5"><span className="font-medium">{p.name}</span> <span className="font-mono text-xs text-page-muted ml-2">{p.slug}</span></td>
                  <td className="px-5 py-2.5 text-right whitespace-nowrap">
                    {canAdmin && (
                      <div className="inline-flex items-center gap-2">
                        <button onClick={() => setMoving(p)} disabled={!moveTargets.length} data-testid={`move-project-${p.slug}`}
                          title={moveTargets.length ? "Move this project to another organization" : "There is no other organization you administer"}
                          className="inline-flex items-center gap-1.5 h-8 px-3 rounded-[5px] border border-page-divider text-xs font-medium hover:bg-page-bg disabled:opacity-40">
                          <ArrowRight size={13}/> Move…
                        </button>
                        <button onClick={() => setDuplicating(p)} data-testid={`duplicate-project-${p.slug}`}
                          title="Make a full, independent copy of this project — here or in another organization"
                          className="inline-flex items-center gap-1.5 h-8 px-3 rounded-[5px] border border-page-divider text-xs font-medium hover:bg-page-bg">
                          <Copy size={13}/> Duplicate…
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {moving && (
        <MoveProjectModal project={moving} fromOrg={org} targets={moveTargets}
          onClose={() => setMoving(null)} onSuccess={() => { setMoving(null); onChanged(); }}/>
      )}
      {duplicating && (
        <DuplicateProjectModal project={duplicating} fromOrg={org} targets={copyTargets}
          onClose={() => { setDuplicating(null); onChanged(); }}/>
      )}

      {showAdd && (
        <AddMemberModal
          orgSlug={org.slug}
          isPlatformAdmin={isPlatformAdmin}
          onClose={() => setShowAdd(false)}
          onSuccess={() => { setShowAdd(false); loadMembers(); onChanged(); }}
        />
      )}
      {showRename && (
        <RenameOrgModal org={org} onClose={() => setShowRename(false)}
          onSuccess={() => { setShowRename(false); onChanged(); }} />
      )}
      {showProvider && (
        <ProviderModal org={org} onClose={() => setShowProvider(false)}
          onSuccess={() => { setShowProvider(false); onChanged(); }} />
      )}
      {showDeleteOrg && (
        <DeleteOrgModal org={org} onClose={() => setShowDeleteOrg(false)}
          onSuccess={() => { setShowDeleteOrg(false); onChanged(); }} />
      )}
      {editingMember && (
        <EditMemberModal orgSlug={org.slug} member={editingMember}
          onClose={() => setEditingMember(null)}
          onSuccess={() => { setEditingMember(null); loadMembers(); }} />
      )}
    </div>
  );
}

function MoveProjectModal({ project, fromOrg, targets, onClose, onSuccess }) {
  const [to, setTo] = useState(targets[0]?.slug || "");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);
  const target = targets.find((o) => o.slug === to);
  const submit = async () => {
    setSubmitting(true); setErr(null);
    try { await moveProjectToOrg(project.slug, to); onSuccess(); }
    catch (e) { setErr(e?.message || "Move failed"); }
    finally { setSubmitting(false); }
  };
  return (
    <ModalShell title={`Move ${project.name}`} onClose={onClose}
      footer={<>
        <button onClick={onClose} className="px-3 py-1.5 rounded text-sm hover:bg-page-bg">Cancel</button>
        <button disabled={!to || submitting} onClick={submit} data-testid="move-project-submit"
          className="px-3 py-1.5 rounded bg-nav-active text-white text-sm font-medium disabled:opacity-50">
          {submitting ? "Moving…" : `Move to ${target ? target.name : "…"}`}
        </button>
      </>}>
      <label className="text-sm block">
        <span className="block text-page-muted mb-1">From <strong>{fromOrg.name}</strong> to</span>
        <select value={to} onChange={(e) => setTo(e.target.value)} data-testid="move-project-target"
          className="w-full h-9 px-3 rounded border border-page-divider bg-page-bg text-sm">
          {targets.map((o) => <option key={o.slug} value={o.slug}>{o.name} ({o.slug})</option>)}
        </select>
      </label>
      <ul className="text-xs text-page-muted mt-3 space-y-1 list-disc pl-4">
        <li>The project keeps its slug, data, apps, workflows and history — only the owning organization changes.</li>
        <li>Members of <strong>{target ? target.name : "the new organization"}</strong> get access by their roles there; members of {fromOrg.name} lose it (unless the project is shared with them).</li>
        <li>Its apps, Board cards, agents, models and reports move with it.</li>
      </ul>
      {err && <div className="text-sm text-red-700 mt-3">{err}</div>}
    </ModalShell>
  );
}

const slugify = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "").replace(/^([0-9])/, "p_$1").slice(0, 60);
const STEP_ICON = { done: <CheckCircle2 size={15} className="text-emerald-600 shrink-0"/>, warn: <AlertTriangle size={15} className="text-amber-600 shrink-0"/>,
  failed: <X size={15} className="text-red-600 shrink-0"/>, running: <Loader2 size={15} className="animate-spin text-nav-active shrink-0"/>, pending: <Circle size={15} className="text-page-muted shrink-0"/>, skipped: <Circle size={15} className="text-page-muted shrink-0"/> };

function DuplicateProjectModal({ project, fromOrg, targets, onClose }) {
  const [name, setName] = useState(`${project.name} (copy)`);
  const [slug, setSlug] = useState(slugify(`${project.slug}_copy`));
  const [slugTouched, setSlugTouched] = useState(false);
  const [to, setTo] = useState(fromOrg.slug);
  const [includeData, setIncludeData] = useState(true);
  const [job, setJob] = useState(null);
  const [err, setErr] = useState(null);
  const [starting, setStarting] = useState(false);
  useEffect(() => {
    if (!job || !["running"].includes(job.status)) return undefined;
    const t = setInterval(() => { getProjectJob(job.id).then(setJob).catch((e) => setErr(e?.message || String(e))); }, 2000);
    return () => clearInterval(t);
  }, [job?.id, job?.status]); // eslint-disable-line
  const start = async () => {
    setStarting(true); setErr(null);
    try { const r = await duplicateProject(project.slug, { slug, name: name.trim(), org_slug: to, include_data: includeData }); setJob(r.job); }
    catch (e) { setErr(e?.message || "Duplicate failed to start"); }
    finally { setStarting(false); }
  };
  const done = job && job.status !== "running";
  const ok = job && /^done/.test(job.status);
  const target = targets.find((o) => o.slug === to) || fromOrg;
  return (
    <ModalShell title={`Duplicate ${project.name}`} onClose={job && !done ? () => {} : onClose}
      footer={!job ? <>
        <button onClick={onClose} className="px-3 py-1.5 rounded text-sm hover:bg-page-bg">Cancel</button>
        <button disabled={!name.trim() || !/^[a-z][a-z0-9_]*$/.test(slug) || starting} onClick={start} data-testid="duplicate-project-submit"
          className="px-3 py-1.5 rounded bg-nav-active text-white text-sm font-medium disabled:opacity-50">
          {starting ? "Starting…" : "Duplicate"}
        </button>
      </> : <>
        {ok && <a href={`/p/${job.to}/b/main`} data-testid="duplicate-open" className="px-3 py-1.5 rounded border border-page-divider text-sm font-medium hover:bg-page-bg">Open {job.to}</a>}
        <button disabled={!done} onClick={onClose} className="px-3 py-1.5 rounded bg-nav-active text-white text-sm font-medium disabled:opacity-50">{done ? "Close" : "Working…"}</button>
      </>}>
      {!job ? (
        <div className="space-y-3 text-sm">
          <label className="block"><span className="block text-page-muted mb-1">Name of the copy</span>
            <input autoFocus value={name} onChange={(e) => { setName(e.target.value); if (!slugTouched) setSlug(slugify(e.target.value)); }} data-testid="duplicate-name"
              className="w-full h-9 px-3 rounded border border-page-divider bg-page-bg outline-none focus:border-nav-active"/></label>
          <label className="block"><span className="block text-page-muted mb-1">Slug (its address, database and data schema name)</span>
            <input value={slug} onChange={(e) => { setSlug(e.target.value.toLowerCase()); setSlugTouched(true); }} data-testid="duplicate-slug"
              className="w-full h-9 px-3 rounded border border-page-divider bg-page-bg font-mono text-xs outline-none focus:border-nav-active"/>
            {!/^[a-z][a-z0-9_]*$/.test(slug) && <span className="text-xs text-red-700">lowercase letters, digits and underscores, starting with a letter</span>}</label>
          <label className="block"><span className="block text-page-muted mb-1">Organization</span>
            <select value={to} onChange={(e) => setTo(e.target.value)} data-testid="duplicate-org"
              className="w-full h-9 px-3 rounded border border-page-divider bg-page-bg text-sm">
              {targets.map((o) => <option key={o.slug} value={o.slug}>{o.name} ({o.slug}){o.slug === fromOrg.slug ? " — same organization" : ""}</option>)}
            </select></label>
          <label className="flex items-center gap-2"><input type="checkbox" checked={includeData} onChange={(e) => setIncludeData(e.target.checked)} data-testid="duplicate-data"/>
            Copy the data too (all rows of the data schema) — off = the same tables, empty</label>
          <div className="text-xs text-page-muted rounded border border-page-divider p-2.5 space-y-1">
            <div><strong>Copied:</strong> the recipe (sources, elements, rules, pipeline), the data schema with its tables, workflows and triggers, every endpoint, service and app service (deployed again), the apps and their views, agents, models, the vault, tests, app titles and Docs.</div>
            <div><strong>Not copied:</strong> the Board history, shares and grants, test runs and fleet sessions, Dolt branches other than main. You own the copy; it starts private.</div>
          </div>
        </div>
      ) : (
        <div className="space-y-2" data-testid="duplicate-progress">
          <div className="text-sm">{project.slug} → <strong className="font-mono">{job.to}</strong> in {target.name}</div>
          {job.steps.map((s) => (
            <div key={s.key} className="flex items-start gap-2 text-sm" data-testid={`duplicate-step-${s.key}`} data-state={s.state}>
              {STEP_ICON[s.state] || STEP_ICON.pending}
              <div className="min-w-0"><div className={s.state === "pending" || s.state === "skipped" ? "text-page-muted" : ""}>{s.label}{s.state === "skipped" ? " — skipped" : ""}</div>
                {s.detail && <div className="text-xs text-page-muted break-words">{s.detail}</div>}</div>
            </div>
          ))}
          {job.status === "failed" && <div className="text-sm text-red-700 rounded bg-red-50 px-3 py-2">The copy stopped: {job.error}. {job.result ? `Project ${job.to} exists with what was copied so far — open it to check, or delete it and try again.` : "Nothing was created."}</div>}
          {job.status === "done_with_warnings" && <div className="text-sm text-amber-800 rounded bg-amber-50 px-3 py-2">Copied, with notes above — check them in the new project.</div>}
          {job.status === "done" && <div className="text-sm text-emerald-800 rounded bg-emerald-50 px-3 py-2">The copy is ready.</div>}
        </div>
      )}
      {err && <div className="text-sm text-red-700 mt-3">{err}</div>}
    </ModalShell>
  );
}

function RenameOrgModal({ org, onClose, onSuccess }) {
  const [name, setName] = useState(org.name);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);
  const submit = async () => {
    setSubmitting(true); setErr(null);
    try { await updateOrg(org.slug, { name: name.trim() }); onSuccess(); }
    catch (e) { setErr(e?.message || "Rename failed"); }
    finally { setSubmitting(false); }
  };
  return (
    <ModalShell title={`Rename ${org.slug}`} onClose={onClose}
      footer={<>
        <button onClick={onClose} className="px-3 py-1.5 rounded text-sm hover:bg-page-bg">Cancel</button>
        <button disabled={!name.trim() || submitting} onClick={submit} data-testid="rename-org-submit"
          className="px-3 py-1.5 rounded bg-nav-active text-white text-sm font-medium disabled:opacity-50">
          {submitting ? "Saving…" : "Save"}
        </button>
      </>}>
      <label className="text-sm block">
        <span className="block text-page-muted mb-1">Display name</span>
        <input autoFocus value={name} onChange={(e) => setName(e.target.value)} data-testid="rename-org-name"
          className="w-full h-9 px-3 rounded border border-page-divider bg-page-bg outline-none focus:border-nav-active"/>
      </label>
      {err && <div className="text-sm text-red-700 mt-2">{err}</div>}
    </ModalShell>
  );
}

function DeleteOrgModal({ org, onClose, onSuccess }) {
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);
  const submit = async () => {
    setSubmitting(true); setErr(null);
    try { await deleteOrg(org.slug); onSuccess(); }
    catch (e) { setErr(e?.message || "Delete failed"); }
    finally { setSubmitting(false); }
  };
  return (
    <ModalShell title={`Delete organization ${org.slug}`} onClose={onClose}
      footer={<>
        <button onClick={onClose} className="px-3 py-1.5 rounded text-sm hover:bg-page-bg">Cancel</button>
        <button disabled={submitting} onClick={submit} data-testid="delete-org-submit"
          className="px-3 py-1.5 rounded bg-red-600 text-white text-sm font-medium disabled:opacity-50">
          {submitting ? "Deleting…" : "Delete organization"}
        </button>
      </>}>
      <p className="text-sm">
        Permanently delete <strong>{org.name}</strong> and all its memberships? The organization must not own any projects — move or delete them first. User accounts are kept.
      </p>
      {err && <div className="text-sm text-red-700 mt-3">{err}</div>}
    </ModalShell>
  );
}

// Configure the org's attribute provider — the integration point with a
// customer's EXISTING policies / entitlement API. Attributes fetched from it
// merge into each user's subject bag and drive the data-access policies.
function ProviderModal({ org, onClose, onSuccess, tutorialDemo }) {
  const existing = org.attribute_provider || null;
  const [kind, setKind] = useState(existing?.kind || (tutorialDemo ? "rest" : "static"));
  const [url, setUrl] = useState(existing?.url || (tutorialDemo ? "https://policies.example.com/api/entitlements?user={email}" : ""));
  const [headers, setHeaders] = useState(existing?.headers ? JSON.stringify(existing.headers, null, 2) : (tutorialDemo ? '{"Authorization": "Bearer <api-key>"}' : "{}"));
  const [ttl, setTtl] = useState(existing?.ttl_seconds || 300);
  const [path, setPath] = useState(existing?.attributes_path || (tutorialDemo ? "data.entitlements" : ""));
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async () => {
    if (tutorialDemo) { onClose(); return; } // display-only in a tutorial
    setSubmitting(true); setErr(null);
    try {
      let parsedHeaders = {};
      if (kind === "rest") {
        try { parsedHeaders = JSON.parse(headers || "{}"); }
        catch { throw new Error("Headers must be valid JSON"); }
      }
      await setAttributeProvider(org.slug, kind === "static"
        ? { kind: "static" }
        : { kind: "rest", url: url.trim(), headers: parsedHeaders, ttl_seconds: Number(ttl) || 300, attributes_path: path.trim() || null });
      onSuccess();
    } catch (e) {
      setErr(e?.message || "Save failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalShell title={`Policy integration — ${org.name}`} onClose={onClose} tutorialDemo={tutorialDemo}
      footer={<>
        <button onClick={onClose} className="px-3 py-1.5 rounded text-sm hover:bg-page-bg">Cancel</button>
        <button disabled={submitting || (kind === "rest" && !/^https?:\/\//.test(url))} onClick={submit} data-testid="provider-submit"
          className="px-3 py-1.5 rounded bg-nav-active text-white text-sm font-medium disabled:opacity-50">
          {submitting ? "Saving…" : "Save"}
        </button>
      </>}>
      <div className="flex flex-col gap-3">
        <p className="text-xs text-page-muted">
          Where user attributes come from when data-access policies are evaluated. <strong>Static</strong> uses only the attributes set per member here. <strong>External API</strong> additionally calls the customer's existing entitlement/RBAC endpoint per user and merges the returned JSON into the subject.
        </p>
        <label className="text-sm">
          <span className="block text-page-muted mb-1">Source</span>
          <select value={kind} onChange={(e) => setKind(e.target.value)} data-testid="provider-kind"
            className="w-full h-9 px-2 rounded border border-page-divider bg-page-bg text-sm">
            <option value="static">Static — member attributes only</option>
            <option value="rest">External API — customer's policies endpoint</option>
          </select>
        </label>
        {kind === "rest" && (
          <>
            <label className="text-sm">
              <span className="block text-page-muted mb-1">Endpoint URL <span className="text-xs">(<code className="font-mono">{"{email}"}</code> is substituted per user)</span></span>
              <input value={url} onChange={(e) => setUrl(e.target.value)} data-testid="provider-url"
                placeholder="https://policies.customer.com/api/entitlements?user={email}"
                className="w-full h-9 px-3 rounded border border-page-divider bg-page-bg outline-none focus:border-nav-active font-mono text-xs"/>
            </label>
            <label className="text-sm">
              <span className="block text-page-muted mb-1">Headers (JSON — e.g. an API key)</span>
              <textarea value={headers} onChange={(e) => setHeaders(e.target.value)} rows={2}
                className="w-full px-3 py-2 rounded border border-page-divider bg-page-bg outline-none focus:border-nav-active font-mono text-xs"/>
            </label>
            <div className="grid grid-cols-2 gap-3">
              <label className="text-sm">
                <span className="block text-page-muted mb-1">Cache TTL (seconds)</span>
                <input type="number" value={ttl} onChange={(e) => setTtl(e.target.value)}
                  className="w-full h-9 px-3 rounded border border-page-divider bg-page-bg outline-none focus:border-nav-active font-mono text-xs"/>
              </label>
              <label className="text-sm">
                <span className="block text-page-muted mb-1">Attributes path (optional, e.g. data.entitlements)</span>
                <input value={path} onChange={(e) => setPath(e.target.value)}
                  className="w-full h-9 px-3 rounded border border-page-divider bg-page-bg outline-none focus:border-nav-active font-mono text-xs"/>
              </label>
            </div>
          </>
        )}
        {err && <div className="text-sm text-red-700">{err}</div>}
      </div>
    </ModalShell>
  );
}

function EditMemberModal({ orgSlug, member, onClose, onSuccess }) {
  const [firstName, setFirstName] = useState(member.first_name || "");
  const [lastName, setLastName] = useState(member.last_name || "");
  const [password, setPassword] = useState("");
  const [attributes, setAttributes] = useState(JSON.stringify(member.attributes || {}, null, 2));
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async () => {
    setSubmitting(true); setErr(null);
    try {
      let parsedAttrs;
      try { parsedAttrs = JSON.parse(attributes || "{}"); }
      catch { throw new Error("Attributes must be valid JSON"); }
      await updateOrgMember(orgSlug, member.email, {
        first_name: firstName, last_name: lastName,
        password: password || undefined,
        attributes: parsedAttrs,
      });
      onSuccess();
    } catch (e) {
      setErr(e?.message || "Save failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalShell title={`Edit member — ${member.email}`} onClose={onClose}
      footer={<>
        <button onClick={onClose} className="px-3 py-1.5 rounded text-sm hover:bg-page-bg">Cancel</button>
        <button disabled={submitting} onClick={submit} data-testid="edit-member-submit"
          className="px-3 py-1.5 rounded bg-nav-active text-white text-sm font-medium disabled:opacity-50">
          {submitting ? "Saving…" : "Save"}
        </button>
      </>}>
      <div className="flex flex-col gap-3">
        <div className="grid grid-cols-2 gap-3">
          <label className="text-sm">
            <span className="block text-page-muted mb-1">First name</span>
            <input value={firstName} onChange={(e) => setFirstName(e.target.value)}
              className="w-full h-9 px-3 rounded border border-page-divider bg-page-bg outline-none focus:border-nav-active"/>
          </label>
          <label className="text-sm">
            <span className="block text-page-muted mb-1">Last name</span>
            <input value={lastName} onChange={(e) => setLastName(e.target.value)}
              className="w-full h-9 px-3 rounded border border-page-divider bg-page-bg outline-none focus:border-nav-active"/>
          </label>
        </div>
        <label className="text-sm">
          <span className="block text-page-muted mb-1">New password <span className="text-xs">(leave empty to keep current)</span></span>
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
            className="w-full h-9 px-3 rounded border border-page-divider bg-page-bg outline-none focus:border-nav-active"/>
        </label>
        <label className="text-sm">
          <span className="block text-page-muted mb-1">Subject attributes <span className="text-xs">(JSON — referenced by data-access policies as <code className="font-mono">:subject.&lt;attr&gt;</code>)</span></span>
          <textarea value={attributes} onChange={(e) => setAttributes(e.target.value)} rows={4} data-testid="member-attributes"
            placeholder='{"region": "EU", "account_ids": [17, 23]}'
            className="w-full px-3 py-2 rounded border border-page-divider bg-page-bg outline-none focus:border-nav-active font-mono text-xs"/>
        </label>
        {err && <div className="text-sm text-red-700">{err}</div>}
      </div>
    </ModalShell>
  );
}

function ModalShell({ title, onClose, children, footer, tutorialDemo }) {
  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-start md:items-center justify-center p-4 overflow-y-auto"
      {...(tutorialDemo ? { "data-tutorial-demo-modal": "1" } : {})}
    >
      <div className="bg-card-bg rounded-[8px] shadow-xl w-full max-w-md mt-12 md:mt-0">
        <div className="flex items-center justify-between px-5 py-3 border-b border-page-divider">
          <h2 className="text-lg font-semibold">{title}</h2>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-page-bg"><X size={18}/></button>
        </div>
        <div className="px-5 py-4">{children}</div>
        {footer && <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-page-divider">{footer}</div>}
      </div>
    </div>
  );
}

function CreateOrgModal({ onClose, onSuccess, tutorialDemo }) {
  const [name, setName] = useState(tutorialDemo ? "Example Corp" : "");
  const [slug, setSlug] = useState(tutorialDemo ? "example_corp" : "");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (tutorialDemo) return;
    if (!slug || slug === slug.toLowerCase().replace(/[^a-z0-9_-]/g, "-").replace(/^-+|-+$/g, "")) {
      setSlug(name.toLowerCase().replace(/[^a-z0-9_-]/g, "-").replace(/^-+|-+$/g, "").slice(0, 63));
    }
  }, [name]); // eslint-disable-line

  const valid = useMemo(() => name.trim() && /^[a-z][a-z0-9_-]{0,62}$/.test(slug), [name, slug]);
  const submit = async () => {
    if (tutorialDemo) { onClose(); return; } // display-only in a tutorial
    if (!valid) return;
    setSubmitting(true); setErr(null);
    try { await createOrg({ slug, name: name.trim() }); onSuccess(slug); }
    catch (e) { setErr(e?.message || "Failed to create organization"); }
    finally { setSubmitting(false); }
  };

  return (
    <ModalShell title="Create organization" onClose={onClose} tutorialDemo={tutorialDemo}
      footer={<>
        <button onClick={onClose} className="px-3 py-1.5 rounded text-sm hover:bg-page-bg">Cancel</button>
        <button disabled={!valid || submitting} onClick={submit} data-testid="create-org-submit"
          className="px-3 py-1.5 rounded bg-nav-active text-white text-sm font-medium disabled:opacity-50">
          {submitting ? "Creating…" : "Create"}
        </button>
      </>}>
      <div className="flex flex-col gap-3">
        <label className="text-sm">
          <span className="block text-page-muted mb-1">Name</span>
          <input autoFocus value={name} onChange={(e) => setName(e.target.value)} data-testid="org-name"
            className="w-full h-9 px-3 rounded border border-page-divider bg-page-bg outline-none focus:border-nav-active"/>
        </label>
        <label className="text-sm">
          <span className="block text-page-muted mb-1">Slug</span>
          <input value={slug} onChange={(e) => setSlug(e.target.value)} data-testid="org-slug"
            className="w-full h-9 px-3 rounded border border-page-divider bg-page-bg outline-none focus:border-nav-active font-mono text-xs"/>
        </label>
        {err && <div className="text-sm text-red-700">{err}</div>}
      </div>
    </ModalShell>
  );
}

// The message shown when a person who already has a Cortex account is added:
// a password typed here only creates a NEW account (it would otherwise let an
// org admin take over a login that belongs to other organizations too).
const EXISTING_ACCOUNT_MSG = "This person already has a Cortex account, so the password you typed was not set. They sign in with their existing password, or you can set a new one with Edit member (pencil).";

function AddMemberModal({ orgSlug, isPlatformAdmin, onClose, onSuccess, tutorialDemo }) {
  const [email, setEmail] = useState(tutorialDemo ? "jordan@example.com" : "");
  const [role, setRole] = useState(tutorialDemo ? "app_user" : "member");
  const [password, setPassword] = useState(tutorialDemo ? "set-a-password" : "");
  const [firstName, setFirstName] = useState(tutorialDemo ? "Casey" : "");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);
  // null = unknown (not checked yet / lookup failed), true / false = answer.
  const [exists, setExists] = useState(null);
  // Set after an add whose typed password was NOT applied: the dialog stays
  // open on the explanation instead of closing silently.
  const [ignoredNotice, setIgnoredNotice] = useState(null);

  const roles = isPlatformAdmin ? ["platform_admin", ...ROLE_OPTIONS] : ROLE_OPTIONS;
  const valid = /.+@.+\..+/.test(email);

  // Look the email up while the admin types, so an existing account's
  // password field is disabled before anything is typed into it.
  useEffect(() => {
    if (tutorialDemo) return;
    setExists(null);
    const e = email.trim();
    if (!/.+@.+\..+/.test(e)) return;
    let live = true;
    const t = setTimeout(() => {
      orgAccountExists(orgSlug, e)
        .then((x) => { if (live) setExists(x); })
        .catch(() => { if (live) setExists(null); });
    }, 350);
    return () => { live = false; clearTimeout(t); };
  }, [email, orgSlug, tutorialDemo]);
  useEffect(() => { if (exists) setPassword(""); }, [exists]);

  const submit = async () => {
    if (tutorialDemo) { onClose(); return; } // display-only in a tutorial
    if (!valid) return;
    setSubmitting(true); setErr(null);
    try {
      const res = await addOrgMember(orgSlug, { email: email.trim(), role, password: password || undefined, first_name: firstName || undefined });
      if (res?.existing_account && password && !res?.password_applied) {
        setIgnoredNotice(email.trim());
        return;
      }
      onSuccess();
    } catch (e) {
      setErr(e?.message || "Failed to add member");
    } finally {
      setSubmitting(false);
    }
  };

  if (ignoredNotice) {
    return (
      <ModalShell title={`Add member to ${orgSlug}`} onClose={onSuccess}
        footer={<button onClick={onSuccess} data-testid="add-member-done"
          className="px-3 py-1.5 rounded bg-nav-active text-white text-sm font-medium">OK</button>}>
        <div className="flex flex-col gap-2" data-testid="add-member-password-ignored">
          <p className="text-sm"><span className="font-mono text-xs">{ignoredNotice}</span> was added to {orgSlug}.</p>
          <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2">{EXISTING_ACCOUNT_MSG}</p>
        </div>
      </ModalShell>
    );
  }

  return (
    <ModalShell title={`Add member to ${orgSlug}`} onClose={onClose} tutorialDemo={tutorialDemo}
      footer={<>
        <button onClick={onClose} className="px-3 py-1.5 rounded text-sm hover:bg-page-bg">Cancel</button>
        <button disabled={!valid || submitting} onClick={submit} data-testid="add-member-submit"
          className="px-3 py-1.5 rounded bg-nav-active text-white text-sm font-medium disabled:opacity-50">
          {submitting ? "Adding…" : "Add member"}
        </button>
      </>}>
      <div className="flex flex-col gap-3">
        <label className="text-sm">
          <span className="block text-page-muted mb-1">Email</span>
          <input autoFocus value={email} onChange={(e) => setEmail(e.target.value)} data-testid="member-email"
            className="w-full h-9 px-3 rounded border border-page-divider bg-page-bg outline-none focus:border-nav-active font-mono text-xs"/>
        </label>
        <label className="text-sm">
          <span className="block text-page-muted mb-1">Role</span>
          <select value={role} onChange={(e) => setRole(e.target.value)} data-testid="member-role"
            className="w-full h-9 px-2 rounded border border-page-divider bg-page-bg text-sm">
            {roles.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </label>
        <label className="text-sm">
          <span className="block text-page-muted mb-1">First name (for new accounts)</span>
          <input value={firstName} onChange={(e) => setFirstName(e.target.value)} disabled={exists === true}
            className="w-full h-9 px-3 rounded border border-page-divider bg-page-bg outline-none focus:border-nav-active disabled:opacity-50"/>
        </label>
        <label className="text-sm">
          <span className="block text-page-muted mb-1">Password <span className="text-xs">(only to create a NEW account; leave empty for existing users)</span></span>
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="member-password"
            disabled={exists === true}
            placeholder={exists === true ? "Not needed — this person already has an account" : ""}
            className="w-full h-9 px-3 rounded border border-page-divider bg-page-bg outline-none focus:border-nav-active disabled:opacity-50"/>
        </label>
        {exists === true && (
          <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2" data-testid="member-existing-account">
            This person already has a Cortex account. They sign in with their existing password; you can set a new one later with Edit member (pencil).
          </p>
        )}
        {err && <div className="text-sm text-red-700">{err}</div>}
        <p className="text-xs text-page-muted">Adding an email that already has a membership updates its role. New accounts require a password — share it with the user; they can change it later.</p>
      </div>
    </ModalShell>
  );
}

function PlanAddons({ org, canAdmin }) {
  const [plan, setPlan] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(null);
  const load = async () => {
    setErr(null);
    try { setPlan(await getEntitlements(org.slug)); }
    catch (e) { setPlan(null); setErr(e?.message || "Failed to load the plan"); }
  };
  useEffect(() => { load(); }, [org.slug]); // eslint-disable-line
  const toggle = async (key, enabled) => {
    setBusy(key); setErr(null);
    try { setPlan(await setEntitlements(org.slug, { [key]: enabled })); }
    catch (e) { setErr(e?.message || "Could not change the plan"); }
    finally { setBusy(null); }
  };
  return (
    <div className="bg-card-bg rounded-[8px] overflow-hidden" data-tour="org-plan" data-testid="org-plan">
      <div className="flex items-center justify-between px-5 py-3 border-b border-page-divider">
        <h2 className="text-base font-semibold flex items-center gap-2"><Building2 size={16}/> Plan &amp; add-ons</h2>
        {plan && (
          <span className="text-xs text-page-muted">
            {plan.restricted ? "Some add-ons are switched off" : "Everything included"}
            {!canAdmin && " · set by the platform team"}
          </span>
        )}
      </div>
      {err && <div className="px-5 py-2 text-sm text-red-700">{err}</div>}
      {!plan ? (
        <div className="px-5 py-5 text-sm text-page-muted">Loading…</div>
      ) : (
        <ul className="divide-y divide-page-divider">
          {plan.catalog.map((a) => {
            const on = plan.addons[a.key] !== false;
            return (
              <li key={a.key} className="flex items-start gap-4 px-5 py-3">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium">{a.label}</div>
                  <div className="text-xs text-page-muted">{a.description}</div>
                </div>
                <label className={`inline-flex items-center gap-2 text-xs ${canAdmin ? "cursor-pointer" : "opacity-70"}`}>
                  <input
                    type="checkbox"
                    role="switch"
                    checked={on}
                    disabled={!canAdmin || busy === a.key}
                    onChange={(e) => toggle(a.key, e.target.checked)}
                    data-testid={`addon-${a.key}`}
                  />
                  {on ? "On" : "Off"}
                </label>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
